import PropTypes from 'prop-types';
import { reaction, action, extendObservable } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import AsyncActionStore from '../AsyncActionStore';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import MenuStore from '../MenuStore';
import AdministratorModalStore, { ALL_ROLES } from './AdministratorModalStore';

export default class AdministratorsStore {
    static PropType = PropTypes.shape({
        users: MobxPropTypes.arrayOrObservableArray.isRequired,

        userModal: AdministratorModalStore.PropType.isRequired,
        exitConfirmationModal: FlagStore.PropType.isRequired,
        search: InputStore.PropType.isRequired,
        menu: MenuStore.PropType.isRequired,
        listStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,

        sortBy: PropTypes.func.isRequired,
        totalUsersCount: PropTypes.func.isRequired,
        openModal: PropTypes.func.isRequired,
        load: PropTypes.func.isRequired,
        createUser: PropTypes.func.isRequired,
        updateUser: PropTypes.func.isRequired,
        deleteUser: PropTypes.func.isRequired,
        sendInvitation: PropTypes.func.isRequired,
    });

    constructor(data = { possibleRoles: ALL_ROLES }) {
        extendObservable(this, /** @class AdministratorsStore */{
            originalUsers: [],
            users: [],
            sortedColumn: null,

            apiList: () => Promise.resolve([]),
            apiCreate: () => Promise.resolve([]),
            apiUpdate: () => Promise.resolve({}),
            apiDelete: () => Promise.resolve(true),
            apiInvitation: () => Promise.resolve(true),

            userModal: new AdministratorModalStore(data),
            exitConfirmationModal: new FlagStore(false),
            search: new InputStore(),
            menu: new MenuStore(),
            listStatus: new AsyncActionStore(),
            updateStatus: new AsyncActionStore(),
        }, data);

        reaction(() => this.search.value, this.sortAndFilterUsers);
    }

    sortBy = action((sortedColumn) => {
        this.sortedColumn = { ...sortedColumn };
        this.sortAndFilterUsers();
    });

    totalUsersCount = () => this.originalUsers.length;

    openModal = action((mode, item) => {
        this.menu.close();
        this.userModal.setMode(mode);
        if (item) {
            this.userModal.setValue(item);
        } else {
            this.userModal.setValue(null);
        }
        this.userModal.modal.open();
    });

    load = action(() => {
        if (!this.listStatus.isLoading) {
            this.listStatus.setLoading(this.apiList().then((users) => {
                users = users ? users.map((user) => ({
                    ...user,
                    role: user.authority || user.role || this.userModal.roleSelect.options[0].value,
                })) : [];
                users.sort((user1, user2) => (user2.isPrimary ? 1 : 0) - (user1.isPrimary ? 1 : 0));
                this.originalUsers.replace(users);
                this.users.replace(users);
            }));
        }
        return this.listStatus.promise;
    });

    createUser = action(() => {
        if (!this.updateStatus.isLoading) {
            const user = this.userModal.getValue();

            this.userModal.email.setTouched(false);
            if (!this.userModal.email.isValid) {
                this.userModal.email.focus();
                return Promise.reject();
            }

            this.updateStatus.setLoading(this.apiCreate({ ...user, authority: user.role, role: user.role })
                .then(action((newUser) => {
                    this.originalUsers.replace([{ ...user, id: newUser.id }, ...this.originalUsers]);
                    this.sortAndFilterUsers();
                    this.userModal.modal.close();
                    return newUser;
                })));
        }
        return this.updateStatus.promise;
    });

    updateUser = action(() => {
        if (!this.updateStatus.isLoading) {
            const user = this.userModal.getValue();

            this.userModal.email.setTouched(false);
            if (!this.userModal.email.isValid) {
                this.userModal.email.focus();
                return Promise.reject();
            }

            this.updateStatus.setLoading(this.apiUpdate({ ...user, authority: user.role }))
                .then(action((isUpdated) => {
                    if (isUpdated) {
                        const userIndex = this.originalUsers.findIndex((currentUser) => currentUser.id === user.id);
                        this.originalUsers[userIndex] = { ...this.originalUsers[userIndex], ...user };
                        this.sortAndFilterUsers();
                        this.userModal.modal.close();
                    }
                    return user;
                }));
        }
        return this.updateStatus.promise;
    });

    deleteUser = action(() => {
        if (!this.updateStatus.isLoading) {
            const user = this.userModal.getValue();
            this.updateStatus.setLoading(this.apiDelete(user))
                .then(action((isDeleted) => {
                    if (isDeleted) {
                        const userIndex = this.originalUsers.findIndex((currentUser) => currentUser.id === user.id);
                        this.originalUsers.splice(userIndex, 1);
                        this.sortAndFilterUsers();
                        this.userModal.modal.close();
                    }
                    return user;
                }));
        }
        return this.updateStatus.promise;
    });

    sendInvitation = action((user) => {
        this.menu.close();
        return this.apiInvitation({ id: user.id });
    });

    sortAndFilterUsers = action(() => {
        let currentUsers = [...this.originalUsers];
        if (this.search.value) {
            const value = this.search.value.toLowerCase();
            currentUsers = currentUsers.filter((item) =>
                // Let's copy paste the world. I refuse to refactor this. I'm too triggered.
                (item.analyticsId || '').toLowerCase().indexOf(value) >= 0 ||
                (item.firstName || '').toLowerCase().indexOf(value) >= 0 ||
                (item.lastName || '').toLowerCase().indexOf(value) >= 0 ||
                (item.email || '').toLowerCase().indexOf(value) >= 0);
        }
        if (this.sortedColumn && this.sortedColumn.name) {
            currentUsers = currentUsers.sort((user1, user2) => {
                const value1 = (user1[this.sortedColumn.name] || '').toLowerCase();
                const value2 = (user2[this.sortedColumn.name] || '').toLowerCase();
                return value1.localeCompare(value2) * (this.sortedColumn.desc ? -1 : 1);
            });
        }
        this.users.replace(currentUsers);
    });
}
